import { motion } from "framer-motion";
import { ArrowRight, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fadeUp } from "../utils";

type StorefrontHeroProps = {
  onExploreOffers: () => void;
  onViewMerchants: () => void;
};

export function StorefrontHero({
  onExploreOffers,
  onViewMerchants,
}: StorefrontHeroProps) {
  return (
    <section
      id="hero"
      className="relative overflow-hidden px-4 pb-20 pt-16 sm:px-6 sm:pb-28 sm:pt-24 lg:px-8"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -left-32 -top-32 h-96 w-96 bg-violet-500/20 blur-3xl" />
        <div className="absolute -right-24 top-20 h-80 w-80 bg-cyan-400/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 bg-fuchsia-400/15 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(99,102,241,0.12) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-5xl text-center">
        <motion.h1
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
        >
          <span className="block">Exclusive Offers Curated</span>
          <span className="mt-1 block bg-gradient-to-r from-violet-600 via-fuchsia-500 to-cyan-500 bg-clip-text text-transparent">
            Just for You
          </span>
        </motion.h1>

        <motion.p
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mx-auto mt-4 max-w-2xl text-lg font-semibold text-slate-700 sm:text-xl"
        >
          Save More. Earn More. Enjoy More.
        </motion.p>

        <motion.p
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          Discover limited-time deals, exclusive rewards, and member-only
          promotions from your favorite brands.
        </motion.p>

        <motion.div
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Button
            size="lg"
            onClick={onExploreOffers}
            className="group h-12 min-w-[180px] bg-gradient-to-r from-violet-600 to-indigo-600 px-8 text-base font-semibold shadow-lg shadow-violet-500/30 transition-all hover:scale-[1.02] hover:from-violet-500 hover:to-indigo-500 hover:shadow-xl"
          >
            Explore Offers
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={onViewMerchants}
            className="h-12 min-w-[180px] border-slate-300/80 bg-white/70 px-8 text-base font-semibold backdrop-blur-sm transition-all hover:scale-[1.02] hover:bg-white"
          >
            <Store className="mr-2 h-4 w-4" />
            View Merchants
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
